import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React from 'react';
import { NavLink, useMatch } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BrandMark from 'components/common/BrandMark';
import NotificationBell from 'components/common/NotificationBell';
import AccountMenu from './AccountMenu';
import { useTokens } from 'theme';
import useFeature from 'hooks/useFeature';
import { useAssistantStatus } from 'api/assistant';

export const SIDEBAR_WIDTH = 240;

// `feature` (optional): the flag that must be on for the item to show at all.
const navItemsForLocale = () => [
  { label: translate('shell.home'),   icon: <GridViewOutlinedIcon   fontSize="small" />, path: '/console/dashboard' },
  { label: translate('shell.reports'),    icon: <BarChartOutlinedIcon   fontSize="small" />, path: '/console/reports' },
  { label: translate('shell.sources'), icon: <StorageOutlinedIcon    fontSize="small" />, path: '/console/connectors' },
  { label: translate('data.pipelines3'),  icon: <SubjectOutlinedIcon    fontSize="small" />, path: '/console/pipelines' },
  { label: translate('connections.title'), icon: <KeyOutlinedIcon fontSize="small" />, path: '/console/connections' },
];

// Only affirmative availability for this principal exposes the assistant.
const assistantItem = () => ({
  label: translate('shell.assistant'), icon: <ChatBubbleOutlineIcon fontSize="small" />, path: '/console/assistant',
});

function NavItem({ item }) {
  useTranslation();
  const t = useTokens();
  const match = useMatch({ path: item.path, end: false });
  const isActive = Boolean(match);
  const featureOn = useFeature(item.feature);

  if (item.feature && !featureOn) {
    return null;
  }

  return (
    <ListItemButton
      component={NavLink}
      to={item.path}
      selected={isActive}
      sx={{
        mx: 1.5,
        mb: 0.5,
        px: 1.5,
        py: 0.875,
        borderRadius: '8px',
        color: t.onChrome,
        transition: 'background-color 150ms ease',
        '& .MuiListItemIcon-root': { color: t.onChromeMuted },
        '&:hover': { bgcolor: t.chromeAlt },
        // The lime pill: active item, dark ink on the accent in both modes.
        '&.Mui-selected, &.Mui-selected:hover': {
          bgcolor: t.accent,
          color: t.onAccent,
          '& .MuiListItemIcon-root': { color: t.onAccent },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{
          variant: 'body2',
          fontWeight: isActive ? 700 : 500,
          fontSize: '0.875rem',
        }}
      />
    </ListItemButton>
  );
}

export default function Sidebar({ open = false, onClose }) {
  useTranslation();
  const t = useTokens();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
  const assistant = useAssistantStatus();
  const navItems = assistant.isSuccess && assistant.data?.enabled === true ? [...navItemsForLocale(), assistantItem()] : navItemsForLocale();
  return (
    <Drawer
      variant={desktop ? 'permanent' : 'temporary'}
      open={desktop || open}
      onClose={onClose}
      sx={{
        width: { xs: 0, md: SIDEBAR_WIDTH },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          // On light the rail is its own edge; on dark it needs a hairline.
          borderRight: t.mode === 'dark' ? `1px solid ${t.border}` : 'none',
          bgcolor: t.chrome,
          color: t.onChrome,
          overflow: 'hidden',
          '--nb-scrollbar-thumb': 'rgba(255,255,255,0.35)',
          '--nb-scrollbar-thumb-hover': 'rgba(255,255,255,0.6)',
        },
      }}
    >
      {/* Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 3, height: 64, flexShrink: 0 }}>
        <BrandMark />
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: t.onChrome, whiteSpace: 'nowrap' }}>
          Evolve Analytics
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', pt: 1 }}>
        <List disablePadding>
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </List>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 1.5, flexShrink: 0 }} />

      {/* Foot: the account (project, theme, settings, sign-out) and notifications.
          There is no topbar; this is where its controls live. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 1.25, flexShrink: 0 }}>
        <AccountMenu />
        <NotificationBell />
      </Box>
    </Drawer>
  );
}
